import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { useNavigate, useLocation } from "react-router";
import { cartStore, globalPopupStore, wishlistStore } from "@stores/main";
import { useToast } from '../../contexts/ToastContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import { pageTrackingService } from '../../services/pageTracking';
import Loader from "@components/Loader/Loader";
import Coupon from "@models/coupon";
import Product from "@models/product";
import Header from '../HomeRedesign/components/Header/Header';
import Footer from '../HomeRedesign/components/Footer/Footer';
import ScrollToTop from '../HomeRedesign/components/ScrollToTop/ScrollToTop';
import CartItemRedesign from './components/CartItemRedesign/CartItemRedesign';
import styles from './CartRedesign.module.scss';

const CartRedesign: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, cart } = cartStore;
  const [showLoader, setShowLoader] = useState(true);
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null);
  const [isOrderSummaryOpen, setIsOrderSummaryOpen] = useState(false);
  const { showToast } = useToast();
  
  const soldOutItems = cart.items.filter(item => item.isSoldOut);
  const availableItems = cart.items.filter(item => !item.isSoldOut);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'dark');
  }, []);

  // Always scroll to top when landing on cart page (mount or route change)
  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      const mainContainer = document.querySelector('[data-scroll-container]');
      if (mainContainer) {
        (mainContainer as HTMLElement).scrollTop = 0;
      }
    };
    scrollToTop();
    const timeoutId = setTimeout(scrollToTop, 0);
    return () => clearTimeout(timeoutId);
  }, [location.pathname]);

  // Use coupons from cart response
  useEffect(() => {
    if (cart.coupons && cart.coupons.length > 0) {
      setCoupons(cart.coupons);
    } else {
      setCoupons([]);
    }
  }, [cart.coupons]);

  // Auto-open order summary on mobile/tablet when applicable coupons are available
  useEffect(() => {
    const hasApplicableCoupon = coupons.some(coupon => coupon.isApplicable);
    const isMobileOrTablet = window.innerWidth <= 1024;
    
    if (hasApplicableCoupon && isMobileOrTablet) {
      setIsOrderSummaryOpen(true);
    }

    const handleResize = () => {
      const isMobileOrTablet = window.innerWidth <= 1024;
      if (hasApplicableCoupon && isMobileOrTablet) {
        setIsOrderSummaryOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [coupons]);

  // Track cart view ONCE per visit, after the cart has loaded — the old
  // deps re-emitted on every quantity/remove mutation (and StrictMode).
  const cartViewTrackedRef = useRef(false);
  useEffect(() => {
    if (cartViewTrackedRef.current || cartStore.loading) return;
    cartViewTrackedRef.current = true;
    pageTrackingService.trackCartView(cart.totalItems, cart.totalItemsBuyPriceString);
  }, [cartStore.loading, cart.totalItems, cart.totalItemsBuyPriceString]);

  // Show loader for initial load
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowLoader(false);
    }, 250);
    return () => clearTimeout(timer);
  }, []);

  usePageTitle({
    title: 'Cart | OnePlay Store'
  });

  const handleRemoveAll = () => {
    setShowLoader(true);
    if (!loading && cart.totalUniqueItems > 0) {
      cartStore.removeItemFromCart("all");
    }
    setTimeout(() => setShowLoader(false), 250);
  };

  const handleApplyCoupon = (couponCode: string) => {
    if (appliedCoupon === couponCode) {
      setAppliedCoupon(null);
      pageTrackingService.trackCouponRemoved(couponCode);
      showToast('Coupon removed');
    } else {
      setAppliedCoupon(couponCode);
      showToast(`Coupon "${couponCode}" applied!\nPrice will be updated on the checkout page.`);
      pageTrackingService.trackCouponApply(couponCode, true);
    }
  };

  const goToCheckout = () => {
    setShowLoader(true);
    const checkoutUrl = appliedCoupon 
      ? `/checkout?coupon_code=${encodeURIComponent(appliedCoupon)}`
      : "/checkout";
    
    if (cart.globalMessage) {
      globalPopupStore.show(
        cart.globalMessage,
        undefined,
        cart.canCheckout ? () => {
          pageTrackingService.trackCartCheckout(cart.totalItems, cart.totalItemsBuyPriceString);
          navigate(checkoutUrl);
        } : undefined
      );
    } else if (cart.canCheckout) {
      pageTrackingService.trackCartCheckout(cart.totalItems, cart.totalItemsBuyPriceString);
      navigate(checkoutUrl);
    } else {
    }
    setTimeout(() => setShowLoader(false), 250);
  };

  return (
    <div className={styles.cartPage}>
      <Header />

      {showLoader && (
        <div className="loaderOverlay">
          <Loader size={60} color="#A83AFE" />
        </div>
      )}

      <div className={styles.cartContainer}>
        {cart.totalItems === 0 ? (
          <div className={styles.emptyCart}>
            <div className={styles.emptyCartIcon}>
              <i className="fas fa-shopping-cart"></i>
            </div>
            <h2 className={styles.emptyCartTitle}>Your Cart is Empty</h2>
            <p className={styles.emptyCartSubtitle}>
              Looks like you haven't added anything to your cart yet.
            </p>
            <button 
              className={styles.emptyCartButton}
              onClick={() => navigate("/")}
            >
              Explore Products
            </button>
          </div>
        ) : (
          <>
            <div className={styles.cartHeader}>
              <div className={styles.cartTitleSection}>
                <h1 className={styles.cartTitle}>Shopping Cart</h1>
                <span className={styles.cartItemCount}>
                  {cart.items.length} {cart.items.length === 1 ? 'item' : 'items'}
                </span>
              </div>
              {cart.totalItems > 0 && (
                <button 
                  className={styles.removeAllButton}
                  onClick={handleRemoveAll}
                >
                  <i className="fas fa-trash-alt"></i>
                  Remove All
                </button>
              )}
            </div>

            <div className={styles.cartContent}>
              <div className={styles.cartItemsSection}>
                <div className={styles.cartItemsList}>
                  {availableItems.map((item) => (
                    <CartItemRedesign 
                      key={item.id} 
                      item={item} 
                      setShowLoader={setShowLoader}
                    />
                  ))}
                  {soldOutItems.map((item) => (
                    <CartItemRedesign 
                      key={item.id} 
                      item={item} 
                      setShowLoader={setShowLoader}
                    />
                  ))}
                </div>
              </div>

              {/* Mobile/Tablet Sticky Order Summary */}
              <div className={styles.mobileOrderSummary}>
                <div 
                  className={`${styles.mobileOrderSummaryToggle} ${isOrderSummaryOpen ? styles.open : ''}`}
                  onClick={() => setIsOrderSummaryOpen(!isOrderSummaryOpen)}
                >
                  <div className={styles.mobileOrderSummaryHeader}>
                    <div>
                      <div className={styles.mobileTotalLabel}>Total Payable</div>
                      <div className={styles.mobileTotalAmount}>
                        {cart.totalItemsBuyPriceString}
                      </div>
                    </div>
                    <div className={styles.mobileOrderSummaryActions}>
                      <button
                        className={`${styles.mobileCheckoutButton} ${
                          !cart.canCheckout ? styles.checkoutButtonDisabled : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          goToCheckout();
                        }}
                      >
                        Checkout
                      </button>
                      <i className={`fas fa-chevron-${isOrderSummaryOpen ? 'down' : 'up'}`}></i>
                    </div>
                  </div>
                </div>

                {isOrderSummaryOpen && (
                  <div className={styles.mobileOrderSummaryContent}>
                    <div className={styles.orderSummaryContent}>
                      <h2 className={styles.orderSummaryTitle}>Order Summary</h2>
                      
                      <div className={styles.summaryRow}>
                        <span className={styles.summaryLabel}>
                          Items ({cart.totalItems})
                        </span>
                        <span className={styles.summaryValue}>
                          {cart.totalItemsDisplayPriceString}
                        </span>
                      </div>

                      {cart.totalItemsDiscountValue > 0 && (
                        <div className={styles.summaryRow}>
                          <span className={styles.summaryLabel}>
                            Subscribed user offer applied
                          </span>
                          <span className={styles.summaryDiscount}>
                            -{cart.totalItemsDiscountValueString}
                          </span>
                        </div>
                      )}

                      {cart.payOfferItems.map((item: string, index: number) => (
                        <div key={index} className={styles.summaryRow}>
                          <span className={styles.summaryLabel}>{item}</span>
                        </div>
                      ))}

                      {/* Coupons Section */}
                      {coupons.length > 0 && (
                        <div className={styles.couponsSection}>
                          <div className={styles.couponsHeader}>
                            <h3 className={styles.couponsTitle}>Available Coupons</h3>
                          </div>
                          <div className={styles.couponsList}>
                            {coupons.map((coupon: Coupon) => (
                              <div 
                                key={coupon.id} 
                                className={`${styles.couponItem} ${
                                  coupon.isApplicable ? styles.couponApplicable : styles.couponDisabled
                                } ${appliedCoupon === coupon.code ? styles.couponApplied : ""}`}
                              >
                                <div className={styles.couponContent}>
                                  <div className={`${styles.couponCode} ${
                                    coupon.isApplicable ? styles.couponCodeApplicable : styles.couponCodeDisabled
                                  }`}>
                                    {coupon.code}
                                  </div>
                                  {coupon.title && (
                                    <div className={styles.couponTitle}>{coupon.title}</div>
                                  )}
                                </div>
                                {coupon.isApplicable && (
                                  <button
                                    className={`${styles.couponApplyBtn} ${
                                      appliedCoupon === coupon.code ? styles.couponApplyBtnActive : ""
                                    }`}
                                    onClick={() => handleApplyCoupon(coupon.code)}
                                  >
                                    {appliedCoupon === coupon.code ? "Applied" : "Apply"}
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                          {appliedCoupon && (
                            <div className={styles.appliedCouponMessage}>
                              <i className="fas fa-check-circle"></i>
                              <span>Price will be updated on the checkout page.</span>
                            </div>
                          )}
                        </div>
                      )}

                      <div className={styles.summaryDivider}></div>

                      <div className={styles.totalRow}>
                        <span className={styles.totalLabel}>Total Payable</span>
                        <span className={styles.totalAmount}>
                          {cart.totalItemsBuyPriceString}
                        </span>
                      </div>

                      <button
                        className={`${styles.checkoutButton} ${
                          !cart.canCheckout ? styles.checkoutButtonDisabled : ""
                        }`}
                        onClick={goToCheckout}
                      >
                        Proceed to Checkout
                        <i className="fas fa-arrow-right"></i>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Desktop Order Summary */}
              <div className={styles.orderSummary}>
                <div className={styles.orderSummaryContent}>
                  <h2 className={styles.orderSummaryTitle}>Order Summary</h2>
                  
                  <div className={styles.summaryRow}>
                    <span className={styles.summaryLabel}>
                      Items ({cart.totalItems})
                    </span>
                    <span className={styles.summaryValue}>
                      {cart.totalItemsDisplayPriceString}
                    </span>
                  </div>

                  {cart.totalItemsDiscountValue > 0 && (
                    <div className={styles.summaryRow}>
                      <span className={styles.summaryLabel}>
                        Subscribed user offer applied
                      </span>
                      <span className={styles.summaryDiscount}>
                        -{cart.totalItemsDiscountValueString}
                      </span>
                    </div>
                  )}

                  {cart.payOfferItems.map((item: string, index: number) => (
                    <div key={index} className={styles.summaryRow}>
                      <span className={styles.summaryLabel}>{item}</span>
                    </div>
                  ))}

                  {/* Coupons Section */}
                  {coupons.length > 0 && (
                    <div className={styles.couponsSection}>
                      <div className={styles.couponsHeader}>
                        <h3 className={styles.couponsTitle}>Available Coupons</h3>
                      </div>
                      <div className={styles.couponsList}>
                        {coupons.map((coupon: Coupon) => (
                          <div 
                            key={coupon.id} 
                            className={`${styles.couponItem} ${
                              coupon.isApplicable ? styles.couponApplicable : styles.couponDisabled
                            } ${appliedCoupon === coupon.code ? styles.couponApplied : ""}`}
                          >
                            <div className={styles.couponContent}>
                              <div className={`${styles.couponCode} ${
                                coupon.isApplicable ? styles.couponCodeApplicable : styles.couponCodeDisabled
                              }`}>
                                {coupon.code}
                              </div>
                              {coupon.title && (
                                <div className={styles.couponTitle}>{coupon.title}</div>
                              )}
                            </div>
                            {coupon.isApplicable && (
                              <button
                                className={`${styles.couponApplyBtn} ${
                                  appliedCoupon === coupon.code ? styles.couponApplyBtnActive : ""
                                }`}
                                onClick={() => handleApplyCoupon(coupon.code)}
                              >
                                {appliedCoupon === coupon.code ? "Applied" : "Apply"}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      {appliedCoupon && (
                        <div className={styles.appliedCouponMessage}>
                          <i className="fas fa-check-circle"></i>
                          <span>Price will be updated on the checkout page.</span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className={styles.summaryDivider}></div>

                  <div className={styles.totalRow}>
                    <span className={styles.totalLabel}>Total Payable</span>
                    <span className={styles.totalAmount}>
                      {cart.totalItemsBuyPriceString}
                    </span>
                  </div>

                  <button
                    className={`${styles.checkoutButton} ${
                      !cart.canCheckout ? styles.checkoutButtonDisabled : ""
                    }`}
                    onClick={goToCheckout}
                  >
                    Proceed to Checkout
                    <i className="fas fa-arrow-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default observer(CartRedesign);

